import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import type { TemplateResult } from 'lit';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType } from './_helper.js';
import { getUserByID } from '../api/users.js';
import { getProductByID } from '../api/product.js';
import { getInstitutionByID } from '../api/institution.js';

// Mail template for Lecturer Added to Product

defineMailTemplate(MailTemplateType.LECTURER_ADDED_TO_PRODUCT, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Sie wurden einem Workshop als Dozierender hinzugefügt',
    content: `
            <p> Hallo {{title}} {{firstname}} {{lastname}},</p> <br>
            <p> Sie wurden bei der Institution {{institution-name}} für die Veranstaltung {{product-name}} als Dozierender eingetragen. </p>
            <p>Hier kommen Sie {{login-link}}.</p>
            `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.LECTURER_ADDED_TO_PRODUCT),
  attachments: [],
  displayName: 'Dozent*in Produkt hinzugefügt',
  editorHint: () => institutionMailHint(MailTemplateType.LECTURER_ADDED_TO_PRODUCT),
});

export async function notifyLecturerAddedToProduct(userID: string, productID: string) {
  const user = await getUserByID(userID)({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });
  if (!user) throw new Error('user not found ' + userID);

  const product = await getProductByID(productID)({ institutionID: 1, name: 1 });
  if (!product) throw new Error('product not found ' + productID);

  const institution = await getInstitutionByID(product.institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found ' + product.institutionID);

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    product.institutionID,
    MailTemplateType.LECTURER_ADDED_TO_PRODUCT,
  )(InstitutionMailTemplate.allFields);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const notificationType = await getNotificationType(user, mailTemplate, product.institutionID);
  if (
    notificationType === 'whatsapp' &&
    false /* TODO: remove (always send e-mail until whatsapp notification is implemented) */
  ) {
    throw new Error('not implemented');
  } else if (mailTemplate.emailNotification) {
    const mailVars = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.INSTITUTION_NAME]: institution?.name,
      [MailTemplateVars.PRODUCT_NAME]: product.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      institutionID: institution._id,
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
    });
  }
}
